import {
  FC,
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import {
  Modal,
  TextInput,
  Checkbox,
  Group,
  Stack,
  Text,
  Textarea,
  Flex,
  Badge,
  ScrollArea,
  Paper,
  Tabs,
  Radio,
  Select,
  NumberInput,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  PedigreePersonNodeData,
  PedigreeGeneticData,
  PedigreeCondition,
  PedigreeHeredity,
} from '../types';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { isEqual, isNil } from 'lodash';
import { ConditionForm } from './ConditionForm';
import { GeneticDataForm } from './GeneticDataForm';
import { PedigreeContext } from '../context/PedigreeContext';
import { useGraphData } from '../hooks/useGraphData';

// -----------------------------------------------------------------------------
const DEFAULT_FORM_DATA: PedigreePersonNodeData = {
  label: '',
  gender: undefined,
  conditions: [],
  geneticData: {},
  isPregnancy: false,
  isMultiplePeople: false,
  multiplePeopleCount: undefined,
};

// -----------------------------------------------------------------------------
interface PersonModalProps {
  onClose?: () => void;
}

// -----------------------------------------------------------------------------
export const PersonModal: FC<PersonModalProps> = ({ onClose }) => {
  const { t } = useTranslation('pedigree');
  const { selectedNodeId } = useContext(PedigreeContext) || {};
  const [activeTab, setActiveTab] = useState<string | null>('basic');
  const {
    conditions,
    handleAddCondition,
    getNodeType,
    getNodeData,
    editNodeData,
  } = useGraphData();

  const isOpen = useMemo(
    () => !isNil(selectedNodeId) && getNodeType(selectedNodeId) === 'person',
    [selectedNodeId, getNodeType]
  );

  const person = useMemo(
    () => (isOpen ? getNodeData(selectedNodeId) : null),
    [isOpen, getNodeData, selectedNodeId]
  );

  // ---------------------------------------------------------------------------
  const [formData, setFormData] =
    useState<PedigreePersonNodeData>(DEFAULT_FORM_DATA);
  const [hasUnsavedCondition, setHasUnsavedCondition] = useState(false);

  const baselineRef = useRef<any>(null);
  const formActionsRef = useRef<FormActionButtonsRef>(null);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (person) {
      const newFormData = {
        ...person,
        label:
          person.label ||
          (person.fName || person.lName
            ? `${person.fName || ''} ${person.lName || ''}`
            : ''),
      };

      setFormData(newFormData);
      if (isOpen) {
        baselineRef.current = { formData: newFormData };
      }
    }
  }, [person, isOpen]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) {
      baselineRef.current = null;
    }
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  const toggleCondition = useCallback(
    (conditionId: string) => {
      const currentConditions = formData.conditions || [];
      const hasCondition = currentConditions.some((c) => c.id === conditionId);
      if (hasCondition) {
        setFormData({
          ...formData,
          conditions: currentConditions.filter((c) => c.id !== conditionId),
        });
      } else {
        const conditionToAdd = conditions.find((c) => c.id === conditionId);
        if (conditionToAdd) {
          setFormData({
            ...formData,
            conditions: [...currentConditions, conditionToAdd],
          });
        }
      }
    },
    [formData, conditions]
  );

  // ---------------------------------------------------------------------------
  const handleAddConditionWrapper = useCallback(
    (condition: Omit<PedigreeCondition, 'id'>) => {
      const newCondition = handleAddCondition(condition);
      const currentConditions = formData.conditions || [];
      setFormData({
        ...formData,
        conditions: [...currentConditions, newCondition],
      });
    },
    [formData, handleAddCondition]
  );

  // ---------------------------------------------------------------------------
  const handleSubmit = () => {
    if (hasUnsavedCondition) {
      return;
    }

    editNodeData(selectedNodeId, formData);
    handleClose();
  };

  // ---------------------------------------------------------------------------
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      const current = { formData };
      return !isEqual(current, baselineRef.current);
    } catch {
      return true;
    }
  };

  // ---------------------------------------------------------------------------
  const handleClose = () => {
    onClose?.();
    baselineRef.current = null;
    setFormData(DEFAULT_FORM_DATA);
    setHasUnsavedCondition(false);
  };

  // ---------------------------------------------------------------------------
  const handleDiscardChanges = () => {
    if (person) {
      const resetFormData = { ...person };
      setFormData(resetFormData);
      baselineRef.current = { formData: resetFormData };
    }

    setHasUnsavedCondition(false);
  };

  // ---------------------------------------------------------------------------
  const renderPersonForm = useCallback(
    () => (
      <Stack gap="md">
        {/* Basic Information */}{' '}
        <TextInput
          label={t('personModal.form.nameLabel')}
          value={formData.label}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          placeholder={t('personModal.form.namePlaceholder')}
        />{' '}
        <Radio.Group
          label={t('personModal.form.genderLabel')}
          value={formData.gender}
          onChange={(value) =>
            setFormData({
              ...formData,
              gender: value as 'M' | 'F' | 'U',
            })
          }
        >
          <Group mt="xs">
            <Radio value="M" label={t('personModal.form.genderOptions.male')} />
            <Radio
              value="F"
              label={t('personModal.form.genderOptions.female')}
            />
            <Radio
              value="U"
              label={t('personModal.form.genderOptions.unknown')}
            />
          </Group>
        </Radio.Group>
        <Select
          label={t('personModal.form.heredityLabel')}
          value={formData.heredity || 'none'}
          onChange={(value) =>
            setFormData({
              ...formData,
              heredity: (value as PedigreeHeredity) || 'none',
            })
          }
          data={[
            {
              value: 'none',
              label: t('personModal.form.heredityOptions.none'),
            },
            {
              value: 'childless',
              label: t('personModal.form.heredityOptions.childless'),
            },
            {
              value: 'infertile',
              label: t('personModal.form.heredityOptions.infertile'),
            },
          ]}
          placeholder={t('personModal.form.heredityPlaceholder')}
        />
        <TextInput
          label={t('personModal.form.birthYearLabel')}
          value={formData.birthYear || ''}
          onChange={(e) =>
            setFormData({ ...formData, birthYear: e.target.value })
          }
          placeholder={t('personModal.form.birthYearPlaceholder')}
          type="number"
          min="1900"
          max={new Date().getFullYear()}
        />
        {/* Special Status Checkboxes */}
        <Stack gap="xs">
          <Group justify='space-between'>
            <Checkbox
              label={t('personModal.form.deceasedLabel')}
              checked={formData.isDeceased || false}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  isDeceased: e.currentTarget.checked,
                })
              }
            />
            {formData.isDeceased && (
              <TextInput
                label={t('personModal.form.ageAtDeathLabel')}
                value={formData.ageAtDeath || ''}
                onChange={(e) =>
                  setFormData({ ...formData, ageAtDeath: e.target.value })
                }
                placeholder={t('personModal.form.ageAtDeathPlaceholder')}
                type="number"
                min="0"
                max={'140'}
              />
            )}
          </Group>
          <Checkbox
            label={t('personModal.form.adoptedLabel')}
            checked={formData.isAdopted || false}
            onChange={(e) =>
              setFormData({
                ...formData,
                isAdopted: e.currentTarget.checked,
              })
            }
          />{' '}
          <Checkbox
            label={t('personModal.form.pregnancyLabel')}
            description={t('personModal.form.pregnancyDescription')}
            checked={formData.isPregnancy || false}
            onChange={(e) =>
              setFormData({
                ...formData,
                isPregnancy: e.currentTarget.checked,
                // Clear multiple people when pregnancy is selected
                isMultiplePeople: e.currentTarget.checked
                  ? false
                  : formData.isMultiplePeople,
                multiplePeopleCount: e.currentTarget.checked
                  ? undefined
                  : formData.multiplePeopleCount,
              })
            }
          />
          <Checkbox
            label={t('personModal.form.multiplePeopleLabel')}
            description={t('personModal.form.multiplePeopleDescription')}
            checked={formData.isMultiplePeople || false}
            onChange={(e) =>
              setFormData({
                ...formData,
                isMultiplePeople: e.currentTarget.checked,
                multiplePeopleCount: e.currentTarget.checked ? 2 : undefined,
                // Clear pregnancy when multiple people is selected
                isPregnancy: e.currentTarget.checked
                  ? false
                  : formData.isPregnancy,
              })
            }
          />
          {formData.isMultiplePeople && (
            <NumberInput
              label={t('personModal.form.multiplePeopleCountLabel')}
              placeholder={t('personModal.form.multiplePeopleCountPlaceholder')}
              value={formData.multiplePeopleCount || 2}
              onChange={(value) =>
                setFormData({
                  ...formData,
                  multiplePeopleCount: typeof value === 'number' ? value : 2,
                })
              }
              min={2}
              max={99}
              ml="lg"
            />
          )}
        </Stack>
        {/* Conditions */}
        <Stack gap="xs">
          <Text fw={500} size="sm">
            {t('personModal.form.conditionsLabel')}:
          </Text>
          {conditions.length > 0 && (
            <Paper p="xs" withBorder>
              <ScrollArea.Autosize mah={120}>
                <Stack gap="xs">
                  {conditions.map((condition) => (
                    <Checkbox
                      key={condition.id}
                      label={
                        <Flex align="center" gap="xs">
                          <Badge
                            size="xs"
                            variant="filled"
                            style={{ backgroundColor: condition.color }}
                            circle
                          />
                          <Text size="sm">{condition.name}</Text>
                        </Flex>
                      }
                      checked={
                        formData.conditions?.some(
                          (c) => c.id === condition.id
                        ) || false
                      }
                      onChange={() => toggleCondition(condition.id)}
                    />
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </Paper>
          )}
          {/* Add new condition */}{' '}
          <ConditionForm
            onAddCondition={handleAddConditionWrapper}
            existingConditions={conditions}
            buttonLabel={t('personModal.form.addNewCondition')}
            buttonVariant="outline"
            buttonSize="sm"
            fullWidth
            onUnsavedStateChange={setHasUnsavedCondition}
          />
        </Stack>
        {/* Notes */}{' '}
        <Textarea
          label={t('personModal.form.notesLabel')}
          value={formData.notes || ''}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          placeholder={t('personModal.form.notesPlaceholder')}
          autosize
          minRows={2}
          maxRows={4}
        />
      </Stack>
    ),
    [formData, conditions, handleAddConditionWrapper, toggleCondition, t]
  );

  // ---------------------------------------------------------------------------
  if (!isOpen) {
    return null;
  }

  // ---------------------------------------------------------------------------
  return (
    <>
      {' '}
      <Modal
        opened={isOpen}
        onClose={() => formActionsRef.current?.handleGuardedClose()}
        title={`${t('personModal.title')}: ${formData?.label}`}
        size="md"
        centered
        scrollAreaComponent={ScrollArea.Autosize}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        {' '}
        <Stack gap="md">
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="basic">
                {t('personModal.tabs.basicInformation')}
              </Tabs.Tab>
              <Tabs.Tab value="genetic">
                {t('personModal.tabs.geneticData')}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="basic" pt="md">
              {renderPersonForm()}
            </Tabs.Panel>

            <Tabs.Panel value="genetic" pt="md">
              <GeneticDataForm
                geneticData={formData.geneticData || {}}
                onChange={(geneticData: PedigreeGeneticData) =>
                  setFormData({ ...formData, geneticData })
                }
              />
            </Tabs.Panel>
          </Tabs>
          {/* Buttons */}
          <FormActionButtons
            ref={formActionsRef}
            onSave={handleSubmit}
            onDiscard={handleDiscardChanges}
            onClose={handleClose}
            hasUnsavedChanges={hasUnsavedChanges()}
            isFormValid={!isNil(formData.label) && !hasUnsavedCondition}
            saveLabel={
              hasUnsavedCondition
                ? t('personModal.buttons.saveConditionFirst')
                : t('personModal.buttons.saveChanges')
            }
          />
        </Stack>
      </Modal>
    </>
  );
};
